import type { Post } from '@/@types/post'
import { type SearchResponseBody, SearchResultItemType } from '@/@types/search'
import { SEARCH } from '@/constants/api'
import { cachePosts } from '@/utils/fetchers/post'
import queryClient from '@/utils/queryClient'
import { post } from '@/utils/request'
import { useQuery } from '@tanstack/react-query'
import { isEmpty, isNil } from 'lodash-es'

export default function useSearch(keywords: string) {
	const { data, refetch, isPending, isFetching } = useQuery<SearchResponseBody>({
		queryKey: [SEARCH, keywords],
		queryFn: async () => {
			const prevData = queryClient.getQueryData<SearchResponseBody>([SEARCH, keywords])
			const loadMoreKey = prevData?.loadMoreKey
			const isLoadMoreMode = !isNil(loadMoreKey)

			return post(SEARCH, {
				keywords,
				limit: 20,
				loadMoreKey,
			})
				.then((res) => res.data as SearchResponseBody)
				.then((nextData) => {
					if (isLoadMoreMode) {
						nextData.data = [...(prevData?.data ?? []), ...nextData.data]
					}

					cachePosts(
						nextData.data.filter(
							(item) => item.type === SearchResultItemType.ORIGINAL_POST
						) as unknown as Post[]
					)
					return nextData
				})
		},
		enabled: !isEmpty(keywords),
	})

	return {
		data: data?.data ?? [],
		words: data?.highlightWord?.words ?? [],
		fetchNext: refetch,
		isRefreshing: isPending,
		isFetchingMore: isFetching && !isPending,
	}
}
